// apps/fe/src/modules/catalog/hooks/use-search.ts

'use client';
import { useCallback, useRef, useEffect } from 'react';
import { EventBus } from '@/shared/utils';
import { CatalogEvents, CatalogEventKeys } from '../events';
import { useBaseFilter } from './use-base-filter';
import { getSearchFilteredData } from '../services/search-data-service';
import { CatalogFilters } from '../types/filter';
import { CourseDataFilters } from '../types/filtered-data';

interface UseSearchProps {
  eventBus: EventBus<CatalogEvents>;
  filters: CatalogFilters;
  query?: string;
  withAuthentication?: boolean;
  isEnrichingData?: boolean;
  onFilterChange?: (filters: CourseDataFilters) => void;
  initialActiveFilters?: CourseDataFilters | null;
}

/**
 * Hook for search-specific filtering functionality.
 * Builds on the base filtering hook with search-specific logic.
 */
export function useSearch({
  eventBus,
  filters,
  query,
  withAuthentication = false,
  isEnrichingData = false,
  onFilterChange,
  initialActiveFilters,
}: UseSearchProps) {
  // Debounce reference for search queries
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Use the base filter hook for common filtering functionality
  const baseHook = useBaseFilter({
    eventBus,
    filters,
    fetchDataFn: getSearchFilteredData,
    eventKeys: CatalogEventKeys.search,
    additionalParams: { query },
    useAuthentication: withAuthentication,
    isEnrichingData,
    onFilterChange,
    initialActiveFilters,
  });

  // Search-specific handler that emits proper event - with debounce
  const handleSearch = useCallback(
    ({
      query: incomingQuery,
      filters: incomingFilters,
    }: {
      query?: string;
      filters: Partial<CourseDataFilters>;
    }) => {
      if (!incomingQuery && !query) return;

      // Clear any existing debounce timeout
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }

      // Set a new debounce timeout
      searchDebounceRef.current = setTimeout(() => {
        // Emit the event for search filtering
        eventBus.emit(CatalogEventKeys.search.do, {
          query: incomingQuery || query,
          filters: incomingFilters,
          page: { page: baseHook.page, limit: 25 },
        });

        // Update local state via the base hook
        baseHook.handleFilter({ filters: incomingFilters });

        searchDebounceRef.current = null;
      }, 300); // 300ms debounce
    },
    [baseHook, query, eventBus],
  );

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  return {
    ...baseHook,
    handleSearch,
    searchFilters: baseHook.activeFilters,
    filteredSearchItems: baseHook.filteredItems,
    loadingSearchCatalogData: baseHook.loadingCatalogData,
    parentSearchInstruments: baseHook.parentInstruments,
    instrumentsSearchFilterOnChange: baseHook.instrumentsFilterOnChange,
    skillLevelSearchFilterOnChange: baseHook.skillLevelFilterOnChange,
    musicalGenreSearchFilterOnChange: baseHook.musicalGenreFilterOnChange,
    topicSearchFilterOnChange: baseHook.topicFilterOnChange,
    courseTypeSearchFilterOnChange: baseHook.courseTypeFilterOnChange,
  };
}
